/**
 * ChatApp — WhatsApp-style chat embedded in the Crew Modal
 *
 * Layout: Left sidebar (avatar icon bar) | Right panel (ChatPanel)
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { ChatPanel } from './ChatPanel';
import type { ChatService } from '../../../services/ChatService';
import type { Conversation, StoredMessage } from '../../../db/ChatDatabase';

interface ChatAppEvents {
  'chat:opened': { peerId: string };
  /** Peer identity tapped in the chat header — open their player card. */
  'profile:tap': { playerId: string };
}

export class ChatApp extends EventEmitter<ChatAppEvents> {
  public element: HTMLElement;
  private sidebarEl: HTMLElement;
  private panelContainer: HTMLElement;
  private emptyEl: HTMLElement;
  private chatPanel: ChatPanel | null = null;
  private chatService: ChatService;
  private localPlayerId: string;
  /** Resolve a peer's freshest identity from the crew list (avatar/name). */
  private resolvePeer?: (id: string) => { username?: string; picture?: string } | undefined;
  private activeConversation: string | null = null;
  private conversations: Conversation[] = [];
  private messageHandler: ((msg: StoredMessage) => void) | null = null;
  private conversationsHandler: ((convos: Conversation[]) => void) | null = null;

  constructor(options: {
    chatService: ChatService;
    localPlayerId: string;
    resolvePeer?: (id: string) => { username?: string; picture?: string } | undefined;
  }) {
    super();
    this.chatService = options.chatService;
    this.localPlayerId = options.localPlayerId;
    this.resolvePeer = options.resolvePeer;
    this.element = this.createElement();
    this.sidebarEl = this.element.querySelector('.chat-sidebar')!;
    this.panelContainer = this.element.querySelector('.chat-panel-container')!;
    this.emptyEl = this.element.querySelector('.chat-empty')!;

    this.setupServiceListeners();
  }

  /** Load conversations and render sidebar */
  async load(): Promise<void> {
    this.conversations = await this.chatService.getConversations();
    this.renderSidebar();
  }

  /** Open a specific conversation */
  async openConversation(peerId: string, peerUsername?: string, peerPicture?: string): Promise<void> {
    this.activeConversation = peerId;

    // Find conversation for username. Prefer caller-provided identity (e.g. from
    // the player card) so a brand-new chat has the avatar before any message.
    const convo = this.conversations.find(c => c.peerId === peerId);
    const resolved = this.resolvePeer?.(peerId);
    const username = peerUsername || resolved?.username || convo?.peerUsername || 'Player';
    const picture = peerPicture || resolved?.picture || convo?.peerPicture || '';

    // Destroy existing panel
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }

    // Hide empty, show panel
    this.emptyEl.style.display = 'none';
    this.panelContainer.style.display = 'flex';

    // Create new panel
    this.chatPanel = new ChatPanel({
      localPlayerId: this.localPlayerId,
      peerId,
      peerUsername: username,
      peerPicture: picture,
    });

    // Wire panel events
    this.chatPanel.on('send', async ({ content, type }) => {
      await this.chatService.sendMessage(peerId, username, picture, content, type);
    });

    this.chatPanel.on('profile:tap', (e) => this.emit('profile:tap', e));

    this.chatPanel.on('clear', async () => {
      await this.chatService.deleteConversation(peerId);
      this.chatPanel?.setMessages([]);
      await this.load(); // Refresh sidebar
    });

    this.chatPanel.on('invite', () => {
      // TODO: Open game invite picker
    });

    this.chatPanel.on('image', () => {
      // TODO: Open image picker / camera
    });

    this.panelContainer.innerHTML = '';
    this.panelContainer.appendChild(this.chatPanel.element);

    // Load messages
    const messages = await this.chatService.getMessages(peerId);
    this.chatPanel.setMessages(messages);

    // Mark as read
    await this.chatService.markRead(peerId);

    // Update sidebar active state
    this.updateSidebarActive();

    this.emit('chat:opened', { peerId });
  }

  destroy(): void {
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }
    if (this.messageHandler) {
      this.chatService.off('message:received', this.messageHandler as any);
    }
    if (this.conversationsHandler) {
      this.chatService.off('conversations:updated', this.conversationsHandler as any);
    }
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'chat-app';

    el.innerHTML = `
      <div class="chat-sidebar"></div>
      <div class="chat-panel-container" style="display:none;"></div>
      <div class="chat-empty">
        <div class="crew-empty-icon">💬</div>
        <div class="crew-empty-title">NO CHATS YET</div>
        <div class="crew-empty-text">Open a friend's card and tap Chat to start</div>
      </div>
    `;

    return el;
  }

  private renderSidebar(): void {
    this.sidebarEl.innerHTML = '';

    if (this.conversations.length === 0) {
      this.emptyEl.style.display = this.activeConversation ? 'none' : 'flex';
      this.panelContainer.style.display = this.activeConversation ? 'flex' : 'none';
      return;
    }

    this.emptyEl.style.display = this.activeConversation ? 'none' : 'flex';
    if (!this.activeConversation) {
      this.panelContainer.style.display = 'none';
    }

    for (const convo of this.conversations) {
      const avatar = document.createElement('div');
      avatar.className = `chat-sidebar-avatar ${convo.peerId === this.activeConversation ? 'active' : ''}`;
      avatar.dataset.peerId = convo.peerId;

      const resolved = this.resolvePeer?.(convo.peerId);
      const picture = resolved?.picture || convo.peerPicture;
      const initial = (resolved?.username || convo.peerUsername).charAt(0).toUpperCase();
      if (picture) {
        avatar.innerHTML = `<img src="${picture}" alt="${initial}">`;
      } else {
        avatar.innerHTML = `<span>${initial}</span>`;
      }

      // Unread badge
      if (convo.unreadCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'chat-sidebar-badge';
        badge.textContent = convo.unreadCount > 99 ? '99+' : String(convo.unreadCount);
        avatar.appendChild(badge);
      }

      avatar.addEventListener('click', () => {
        this.openConversation(convo.peerId, convo.peerUsername);
      });

      this.sidebarEl.appendChild(avatar);
    }
  }

  private updateSidebarActive(): void {
    this.sidebarEl.querySelectorAll('.chat-sidebar-avatar').forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.toggle('active', htmlEl.dataset.peerId === this.activeConversation);
    });
  }

  private setupServiceListeners(): void {
    // Live incoming messages
    this.messageHandler = (msg: StoredMessage) => {
      if (this.chatPanel && (msg.peerId === this.activeConversation || msg.from === this.activeConversation)) {
        this.chatPanel.appendMessage(msg);
        // Auto-mark as read if this conversation is open
        if (msg.from !== this.localPlayerId) {
          this.chatService.markRead(this.activeConversation!);
        }
      }
    };
    this.chatService.on('message:received', this.messageHandler as any);

    // Conversation list updates
    this.conversationsHandler = (convos: Conversation[]) => {
      this.conversations = convos;
      this.renderSidebar();
    };
    this.chatService.on('conversations:updated', this.conversationsHandler as any);
  }
}
